/**
 * 組織設定/店舗ページ (/org-settings/store)
 *
 * サイドバーメニュー「組織設定」配下のタブとして表示される。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { TabBar } from "../components/molecules/TabBar";
import { ActionIcon } from "../components/atoms/Icon";
import {
    ColumnFilterPopup,
    useColumnFilter,
    type SortDirection,
} from "../components/molecules/ColumnFilterPopup";
import { useMenuTitle } from "../shared/menu/useMenuTitle";
import { fetchStores, type StoreResponse } from "../api/stores";
import { fetchOrganizations, type OrganizationResponse } from "../api/organizations";
import {
    CommonTable,
    CommonTableHeaderCell,
    CommonTableEditHeaderCell,
    CommonTableRow,
    CommonTableCell,
    CommonTableEditCell,
} from "../components/table";

const STORE_COLUMN_COUNT = 7;
const EMPTY_VALUE_KEY = "__empty__";
const EMPTY_VALUE_LABEL = "(未設定)";

type SortColumn = "code" | "name" | "shortName" | "organization" | "departments" | "status";

const displayValue = (val: string | null | undefined): string =>
    val === null || val === undefined || val === "" ? "-" : val;

const stringKey = (val: string | null | undefined): string =>
    val === null || val === undefined || val === "" ? EMPTY_VALUE_KEY : val;

const labelOrEmpty = (key: string): string =>
    key === EMPTY_VALUE_KEY ? EMPTY_VALUE_LABEL : key;

/** store.status (1=稼働前, 2=稼働中, 3=閉店, 4=その他) のラベル */
const STATUS_LABEL_BY_VALUE: Record<number, string> = {
    1: "稼働前",
    2: "稼働中",
    3: "閉店",
    4: "その他",
};

const statusKey = (status: number): string => String(status);

const statusLabel = (key: string): string =>
    STATUS_LABEL_BY_VALUE[Number(key)] ?? "-";

const OrgSettingsStorePage = () => {
    const menuTitle = useMenuTitle();
    const navigate = useNavigate();
    const [stores, setStores] = useState<StoreResponse[]>([]);
    const [orgs, setOrgs] = useState<OrganizationResponse[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const [storesRes, orgsRes] = await Promise.all([
                    fetchStores(),
                    fetchOrganizations(),
                ]);
                setStores(storesRes.items);
                setOrgs(orgsRes.items);
            } catch (error) {
                console.error("Failed to fetch stores/organizations:", error);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    // organization の id 引きと、親 → 子インデックスを準備
    const orgsById = useMemo(() => {
        const map = new Map<number, OrganizationResponse>();
        for (const o of orgs) map.set(o.id, o);
        return map;
    }, [orgs]);

    const childrenByParentId = useMemo(() => {
        const map = new Map<number, OrganizationResponse[]>();
        for (const o of orgs) {
            const p = o.parent_organization_id;
            if (p === null) continue;
            if (!map.has(p)) map.set(p, []);
            map.get(p)!.push(o);
        }
        return map;
    }, [orgs]);

    // 略称: store が指す level=11 organization の short_name
    const getShortName = useCallback(
        (s: StoreResponse): string | null => s.short_name ?? null,
        [],
    );

    // 所属組織: store の level=11 org から親方向に root まで辿った名前を ">" で連結
    const getOrganizationPath = useCallback(
        (s: StoreResponse): string | null => {
            if (s.organization_id === null) return null;
            const ownOrg = orgsById.get(s.organization_id);
            if (!ownOrg) return null;
            const names: string[] = [];
            let parentId = ownOrg.parent_organization_id;
            while (parentId !== null) {
                const parent = orgsById.get(parentId);
                if (!parent) break;
                if (parent.name) names.unshift(parent.name);
                parentId = parent.parent_organization_id;
            }
            return names.join(" > ") || null;
        },
        [orgsById],
    );

    // 使用部門: store の level=11 org の子 (level=12) の名前を ", " で連結
    const getDepartmentsLabel = useCallback(
        (s: StoreResponse): string => {
            if (s.organization_id === null) return "";
            const children = childrenByParentId.get(s.organization_id) ?? [];
            return children
                .filter((c) => c.level === 12)
                .map((c) => c.name ?? "")
                .filter(Boolean)
                .join(", ");
        },
        [childrenByParentId],
    );

    // 列ごとの並び替え/絞り込み state
    const codeFilter = useColumnFilter(stores, (s) => stringKey(s.code), labelOrEmpty);
    const nameFilter = useColumnFilter(stores, (s) => stringKey(s.name), labelOrEmpty);
    const shortNameFilter = useColumnFilter(stores, (s) => stringKey(getShortName(s)), labelOrEmpty);
    const organizationFilter = useColumnFilter(stores, (s) => stringKey(getOrganizationPath(s)), labelOrEmpty);
    const statusFilter = useColumnFilter(stores, (s) => statusKey(s.status), statusLabel);

    // 使用部門は他の列と異なり、店舗ごとの「組み合わせ」ではなく
    // 「個々の部門名」を選択肢にする。選択された部門を「含む」店舗を絞り込み対象とする
    const getDepartmentNames = useCallback(
        (s: StoreResponse): string[] => {
            if (s.organization_id === null) return [];
            const children = childrenByParentId.get(s.organization_id) ?? [];
            return children
                .filter((c) => c.level === 12)
                .map((c) => c.name ?? "")
                .filter(Boolean);
        },
        [childrenByParentId],
    );
    const allDepartmentNames = useMemo(() => {
        const set = new Set<string>();
        for (const s of stores) {
            for (const n of getDepartmentNames(s)) set.add(n);
        }
        return Array.from(set).sort((a, b) => a.localeCompare(b, "ja"));
    }, [stores, getDepartmentNames]);
    const [deptSelected, setDeptSelected] = useState<string[]>([]);
    const [deptOpen, setDeptOpen] = useState(false);
    const [deptReady, setDeptReady] = useState(false);
    const [deptUserModified, setDeptUserModified] = useState(false);
    const deptAnchorRef = useRef<HTMLElement | null>(null);
    useEffect(() => {
        if (allDepartmentNames.length > 0 && !deptReady) {
            setDeptSelected(allDepartmentNames);
            setDeptReady(true);
        }
    }, [allDepartmentNames, deptReady]);
    const deptOptions = useMemo(
        () => allDepartmentNames.map((n) => ({ value: n, label: n })),
        [allDepartmentNames],
    );
    const deptIsFiltering =
        deptReady && deptUserModified && deptSelected.length < allDepartmentNames.length;
    const deptFilterCount =
        !deptReady ||
        !deptUserModified ||
        deptSelected.length === allDepartmentNames.length
            ? undefined
            : deptSelected.length;
    const departmentsFilter = useMemo(
        () => ({
            open: deptOpen,
            setOpen: setDeptOpen,
            anchorRef: deptAnchorRef,
            filterCount: deptFilterCount,
        }),
        [deptOpen, deptFilterCount],
    );

    // 並び替えは「同時に1列のみ」アクティブ
    const [activeSort, setActiveSort] = useState<{
        column: SortColumn;
        direction: "asc" | "desc";
    } | null>(null);

    const sortFor = (column: SortColumn): SortDirection =>
        activeSort?.column === column ? activeSort.direction : null;

    const handleSortChange = useCallback(
        (column: SortColumn) => (dir: SortDirection) => {
            setActiveSort(dir === null ? null : { column, direction: dir });
        },
        [],
    );

    // 表示用にソート/絞り込みを適用
    const displayedStores = useMemo(() => {
        let list = stores;

        if (codeFilter.isFiltering) {
            const set = new Set(codeFilter.selected);
            list = list.filter((s) => set.has(stringKey(s.code)));
        }
        if (nameFilter.isFiltering) {
            const set = new Set(nameFilter.selected);
            list = list.filter((s) => set.has(stringKey(s.name)));
        }
        if (shortNameFilter.isFiltering) {
            const set = new Set(shortNameFilter.selected);
            list = list.filter((s) => set.has(stringKey(getShortName(s))));
        }
        if (organizationFilter.isFiltering) {
            const set = new Set(organizationFilter.selected);
            list = list.filter((s) => set.has(stringKey(getOrganizationPath(s))));
        }
        if (deptIsFiltering) {
            // 選択された部門名のいずれかを含む店舗を残す
            const selectedSet = new Set(deptSelected);
            list = list.filter((s) =>
                getDepartmentNames(s).some((n) => selectedSet.has(n)),
            );
        }
        if (statusFilter.isFiltering) {
            const set = new Set(statusFilter.selected);
            list = list.filter((s) => set.has(statusKey(s.status)));
        }

        if (activeSort) {
            const cmpString = (
                getter: (s: StoreResponse) => string | null,
            ) => (a: StoreResponse, b: StoreResponse): number =>
                (getter(a) ?? "").localeCompare(getter(b) ?? "", "ja");
            const cmpStatus = (a: StoreResponse, b: StoreResponse): number =>
                a.status - b.status;
            const cmpByColumn: Record<SortColumn, (a: StoreResponse, b: StoreResponse) => number> = {
                code: cmpString((s) => s.code),
                name: cmpString((s) => s.name),
                shortName: cmpString(getShortName),
                organization: cmpString(getOrganizationPath),
                departments: cmpString(getDepartmentsLabel),
                status: cmpStatus,
            };
            const cmp = cmpByColumn[activeSort.column];
            list = [...list].sort((a, b) =>
                activeSort.direction === "asc" ? cmp(a, b) : -cmp(a, b),
            );
        }

        return list;
    }, [
        stores,
        codeFilter.isFiltering,
        codeFilter.selected,
        nameFilter.isFiltering,
        nameFilter.selected,
        shortNameFilter.isFiltering,
        shortNameFilter.selected,
        organizationFilter.isFiltering,
        organizationFilter.selected,
        deptIsFiltering,
        deptSelected,
        getDepartmentNames,
        statusFilter.isFiltering,
        statusFilter.selected,
        activeSort,
    ]);

    // ヘッダークリック時、ポップアップは「同時に1つだけ」開く
    const openOnly = useCallback(
        (which: SortColumn, anchor: HTMLButtonElement) => {
            const map = {
                code: codeFilter,
                name: nameFilter,
                shortName: shortNameFilter,
                organization: organizationFilter,
                departments: departmentsFilter,
                status: statusFilter,
            } as const;
            const target = map[which];
            target.anchorRef.current = anchor;
            const willOpen = !target.open;
            target.setOpen(willOpen);
            if (willOpen) {
                Object.entries(map).forEach(([col, f]) => {
                    if (col !== which) f.setOpen(false);
                });
            }
        },
        [codeFilter, nameFilter, shortNameFilter, organizationFilter, departmentsFilter, statusFilter],
    );

    return (
        <div className="flex flex-1 flex-col overflow-hidden">
            <PageHeader title={menuTitle} />
            <TabBar parentCode="org_settings" />

            {/* アクションボタン */}
            <div className="px-6 pb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        onClick={() => navigate("/org-settings/store/new")}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-brand-500 text-white text-base font-medium hover:bg-brand-600 transition-colors"
                    >
                        <ActionIcon name="add" className="w-4 h-4" />
                        新規追加
                    </button>
                    <button
                        type="button"
                        disabled
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-300 text-gray-500 text-base font-medium cursor-not-allowed"
                    >
                        <ActionIcon name="edit" className="w-4 h-4" />
                        一括編集
                    </button>
                    <button
                        type="button"
                        disabled
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-300 text-gray-500 text-base font-medium cursor-not-allowed"
                    >
                        <ActionIcon name="upload" className="w-4 h-4" />
                        アップロード
                    </button>
                    <button
                        type="button"
                        disabled
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-300 text-gray-500 text-base font-medium cursor-not-allowed"
                    >
                        <ActionIcon name="download" className="w-4 h-4" />
                        ダウンロード
                    </button>
                </div>
            </div>

            {/* 店舗テーブル */}
            <div className="flex-1 min-h-0 px-6 pb-6">
                <CommonTable>
                    <thead className="sticky top-0 z-20">
                        <tr>
                            <CommonTableEditHeaderCell />
                            <CommonTableHeaderCell
                                sortable
                                sortActive={codeFilter.open}
                                sortDirection={sortFor("code")}
                                filterCount={codeFilter.filterCount}
                                onSort={(anchor) => openOnly("code", anchor)}
                                className="min-w-[200px]"
                            >
                                店舗コード
                            </CommonTableHeaderCell>
                            <CommonTableHeaderCell
                                sortable
                                sortActive={nameFilter.open}
                                sortDirection={sortFor("name")}
                                filterCount={nameFilter.filterCount}
                                onSort={(anchor) => openOnly("name", anchor)}
                                className="min-w-[240px]"
                            >
                                店舗名
                            </CommonTableHeaderCell>
                            <CommonTableHeaderCell
                                sortable
                                sortActive={shortNameFilter.open}
                                sortDirection={sortFor("shortName")}
                                filterCount={shortNameFilter.filterCount}
                                onSort={(anchor) => openOnly("shortName", anchor)}
                                className="min-w-[160px]"
                            >
                                略称
                            </CommonTableHeaderCell>
                            <CommonTableHeaderCell
                                sortable
                                sortActive={organizationFilter.open}
                                sortDirection={sortFor("organization")}
                                filterCount={organizationFilter.filterCount}
                                onSort={(anchor) => openOnly("organization", anchor)}
                                className="min-w-[240px]"
                            >
                                所属組織
                            </CommonTableHeaderCell>
                            <CommonTableHeaderCell
                                sortable
                                sortActive={departmentsFilter.open}
                                sortDirection={sortFor("departments")}
                                filterCount={departmentsFilter.filterCount}
                                onSort={(anchor) => openOnly("departments", anchor)}
                                className="min-w-[280px]"
                            >
                                使用部門
                            </CommonTableHeaderCell>
                            <CommonTableHeaderCell
                                sortable
                                sortActive={statusFilter.open}
                                sortDirection={sortFor("status")}
                                filterCount={statusFilter.filterCount}
                                onSort={(anchor) => openOnly("status", anchor)}
                                className="min-w-[120px]"
                            >
                                ステータス
                            </CommonTableHeaderCell>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <CommonTableRow>
                                <CommonTableCell colSpan={STORE_COLUMN_COUNT} className="py-8 text-gray-500">読み込み中...</CommonTableCell>
                            </CommonTableRow>
                        ) : displayedStores.length === 0 ? (
                            <CommonTableRow>
                                <CommonTableCell colSpan={STORE_COLUMN_COUNT} className="py-8 text-gray-500">
                                    {stores.length === 0 ? "店舗が登録されていません" : "該当する店舗がありません"}
                                </CommonTableCell>
                            </CommonTableRow>
                        ) : (
                            displayedStores.map((store) => (
                                <CommonTableRow key={store.id}>
                                    <CommonTableEditCell onClick={() => navigate(`/org-settings/store/${store.id}/edit`)} />
                                    <CommonTableCell>{displayValue(store.code)}</CommonTableCell>
                                    <CommonTableCell>{displayValue(store.name)}</CommonTableCell>
                                    <CommonTableCell>{displayValue(getShortName(store))}</CommonTableCell>
                                    <CommonTableCell>{displayValue(getOrganizationPath(store))}</CommonTableCell>
                                    <CommonTableCell>{displayValue(getDepartmentsLabel(store))}</CommonTableCell>
                                    <CommonTableCell>{statusLabel(statusKey(store.status))}</CommonTableCell>
                                </CommonTableRow>
                            ))
                        )}
                    </tbody>
                </CommonTable>
            </div>

            {/* 列ごとの並び替え＋絞り込みポップアップ */}
            <ColumnFilterPopup
                isOpen={codeFilter.open}
                onClose={() => codeFilter.setOpen(false)}
                anchorRef={codeFilter.anchorRef}
                sortDirection={sortFor("code")}
                onSortChange={handleSortChange("code")}
                options={codeFilter.options}
                selectedValues={codeFilter.selected}
                onSelectedValuesChange={codeFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={nameFilter.open}
                onClose={() => nameFilter.setOpen(false)}
                anchorRef={nameFilter.anchorRef}
                sortDirection={sortFor("name")}
                onSortChange={handleSortChange("name")}
                options={nameFilter.options}
                selectedValues={nameFilter.selected}
                onSelectedValuesChange={nameFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={shortNameFilter.open}
                onClose={() => shortNameFilter.setOpen(false)}
                anchorRef={shortNameFilter.anchorRef}
                sortDirection={sortFor("shortName")}
                onSortChange={handleSortChange("shortName")}
                options={shortNameFilter.options}
                selectedValues={shortNameFilter.selected}
                onSelectedValuesChange={shortNameFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={organizationFilter.open}
                onClose={() => organizationFilter.setOpen(false)}
                anchorRef={organizationFilter.anchorRef}
                sortDirection={sortFor("organization")}
                onSortChange={handleSortChange("organization")}
                options={organizationFilter.options}
                selectedValues={organizationFilter.selected}
                onSelectedValuesChange={organizationFilter.setSelected}
            />
            <ColumnFilterPopup
                isOpen={deptOpen}
                onClose={() => setDeptOpen(false)}
                anchorRef={deptAnchorRef}
                sortDirection={sortFor("departments")}
                onSortChange={handleSortChange("departments")}
                options={deptOptions}
                selectedValues={deptSelected}
                onSelectedValuesChange={(v) => {
                    setDeptUserModified(true);
                    setDeptSelected(v);
                }}
            />
            <ColumnFilterPopup
                isOpen={statusFilter.open}
                onClose={() => statusFilter.setOpen(false)}
                anchorRef={statusFilter.anchorRef}
                sortDirection={sortFor("status")}
                onSortChange={handleSortChange("status")}
                options={statusFilter.options}
                selectedValues={statusFilter.selected}
                onSelectedValuesChange={statusFilter.setSelected}
            />
        </div>
    );
};

export default OrgSettingsStorePage;
